import createUrlBuilder from 'utilities/url.builder.utils';
import { getRequest } from 'utilities/service.utils';
import mapDataService from './mds.service.context';
import AppService from './app.service.context';

const { REACT_APP_MAP_DATA_SERVICE_KEY } = process.env;

export const getGeocodeByCoordinate = async (
  datasets: string,
  lon: string,
  lat: string
) => {
  const accessKey = REACT_APP_MAP_DATA_SERVICE_KEY || '';
  const urlBuilder = createUrlBuilder('/geocodeByCoordinate');
  urlBuilder.addParameter('key', accessKey);
  urlBuilder.addParameter('datasets', datasets);
  urlBuilder.addParameter('latitude', lat);
  urlBuilder.addParameter('longitude', lon);
  return await getRequest(mapDataService, urlBuilder.toString());
};

export const getPlaceSuggest = async (
  datasets: string,
  query: string,
  fields: string,
  limit: string
) => {
  const accessKey = REACT_APP_MAP_DATA_SERVICE_KEY || '';
  const urlBuilder = createUrlBuilder('/place/suggest');
  urlBuilder.addParameter('key', accessKey);
  urlBuilder.addParameter('query', query);
  urlBuilder.addParameter('datasets', datasets);
  urlBuilder.addParameter('fields', fields);
  urlBuilder.addParameter('limit', limit);
  return await getRequest(mapDataService, urlBuilder.toString());
};

export const getFeatureServerUrl = (
  service: string,
  publicMode: boolean = false
) => {
  return `${process.env.REACT_APP_RSOTF_API_URL}/arcgis/rest/services/${
    publicMode ? 'Public/' : ''
  }${service}/MapServer/0`;
};

export const featureRequestInterceptors = async (params: any) => {
  if (params?.requestOptions) {
    params.requestOptions.headers = {};
    await AppService.authenticationHandler(params.requestOptions.headers);
  }
};

export const queryFeatuerLayerExtent = (
  featureLayer: __esri.FeatureLayer,
  query?: string,
  extent?: __esri.Polygon
) => {
  if (query && extent) {
    return featureLayer.queryExtent({
      where: query,
      geometry: extent
    });
  } else if (query) {
    return featureLayer.queryExtent({ where: query });
  } else {
    return featureLayer.queryExtent();
  }
};

export const queryFeatuerLayerFeatures = (
  featureLayer: __esri.FeatureLayer,
  query: any = undefined
) => {
  return featureLayer.queryFeatures(query);
};

export const getHexBinClassBreakStatistics = async (
  featureLayer: __esri.FeatureLayer,
  resolution: number,
  statisticField: string
) => {
  const urlBuilder = createUrlBuilder(
    `${featureLayer.url.split('/api')[1]}/0/query`
  );
  urlBuilder.addParameter(
    'where',
    `h3_rslt = ${resolution} AND ${statisticField} > 0`
  );
  urlBuilder.addParameter('groupByFieldsForStatistics', 'h3_rslt');
  urlBuilder.addParameter('f', 'json');
  urlBuilder.addParameter(
    'outStatistics',
    JSON.stringify([
      {
        statisticType: 'PERCENTILE_CONT',
        statisticParameters: {
          value: 0.2,
          orderBy: 'ASC'
        },
        onStatisticField: statisticField,
        outStatisticFieldName: 'p20'
      },
      {
        statisticType: 'PERCENTILE_CONT',
        statisticParameters: {
          value: 0.4,
          orderBy: 'ASC'
        },
        onStatisticField: statisticField,
        outStatisticFieldName: 'p40'
      },
      {
        statisticType: 'PERCENTILE_CONT',
        statisticParameters: {
          value: 0.6,
          orderBy: 'ASC'
        },
        onStatisticField: statisticField,
        outStatisticFieldName: 'p60'
      },
      {
        statisticType: 'PERCENTILE_CONT',
        statisticParameters: {
          value: 0.8,
          orderBy: 'ASC'
        },
        onStatisticField: statisticField,
        outStatisticFieldName: 'p80'
      }
    ])
  );

  return getRequest(AppService, urlBuilder.toString());
};

export const getBaseMaps = async () => {
  const urlBuilder = createUrlBuilder(
    `/arcgis/rest/services/Basemap_extents/MapServer/0/query`
  );
  const result =
    urlBuilder && (await getRequest(AppService, urlBuilder.toString()));
  return result;
};

export const getGeocodeAddressSuggest = async (
  query: string,
  isAnonymous: boolean,
  limit?: number,
  location?: string,
  category?: string
) => {
  const urlBuilder = createUrlBuilder(
    `/system/geocode/${isAnonymous ? 'anonymous/' : ''}address/suggest`
  );
  urlBuilder.addParameter('query', query);
  limit && urlBuilder.addParameter('limit', limit);
  location && urlBuilder.addParameter('location', location);
  category && urlBuilder.addParameter('category', category);
  return await getRequest(AppService, urlBuilder.toString());
};

export const getPlaceAddressCandidateArcGISWorldGeocoding = async (
  text: string,
  outFields: string = '*',
  magicKey?: string,
  isAnonymous?: boolean
) => {
  const urlBuilder = createUrlBuilder(
    `/system/geocode/${isAnonymous ? 'anonymous/' : ''}findAddressCandidates`
  );
  urlBuilder.addParameter('SingleLine', text);
  urlBuilder.addParameter('outFields', outFields);
  urlBuilder.addParameter('f', 'pjson');
  urlBuilder.addParameter(
    'countryCode',
    process.env.REACT_APP_ARCGIS_WORLD_GEOCODING_SERVICE_BASE_COUNTRY || ''
  );

  magicKey && urlBuilder.addParameter('magicKey', magicKey);

  return getRequest(AppService, urlBuilder.toString());
};

export const getAddressGeoscape = async (
  addressId: string,
  isAnonymous: boolean
) => {
  const urlBuilder = createUrlBuilder(
    `/system/geocode/${
      isAnonymous ? 'anonymous/' : ''
    }v1/predictive/address/${addressId}`
  );
  return getRequest(AppService, urlBuilder.toString());
};

export const getLandParcelGeoscape = async (
  addressId: string,
  isAnonymous: boolean
) => {
  const urlBuilder = createUrlBuilder(
    `/system/geocode/${
      isAnonymous ? 'anonymous/' : ''
    }v1/landParcels/cadastres/findByIdentifier`
  );
  urlBuilder.addParameter('addressId', addressId);
  urlBuilder.addParameter('baseParcel', true);
  return getRequest(AppService, urlBuilder.toString());
};

export const getLandParcelByPoint = async (
  latitude: number,
  longitude: number,
  isAnonymous: boolean
) => {
  const urlBuilder = createUrlBuilder(
    `/system/geocode/${
      isAnonymous ? 'anonymous/' : ''
    }v1/landParcels/cadastres/findByPoint`
  );
  urlBuilder.addParameter('latitude', latitude);
  urlBuilder.addParameter('longitude', longitude);
  urlBuilder.addParameter('radius', 1);
  urlBuilder.addParameter('baseParcel', 'Yes');
  return getRequest(AppService, urlBuilder.toString());
};
